import Link from "next/link";
import {
  calcTotal,
  buildLineItems,
  formatAED,
} from "@/app/calculator/utils/calculatorUtils";
import { StepLabel } from "./Ui";
import { ArrowRight, FileText } from "lucide-react";

export function StepSummary({ data }) {
  const total = calcTotal(data);
  const lineItems = buildLineItems(data);

  const handlePreviewInvoice = async () => {
    try {
      // Dynamically import to avoid Next.js SSR "window is not defined" errors
      const { jsPDF } = await import("jspdf");
      const { default: autoTable } = await import("jspdf-autotable");
      
      const doc = new jsPDF();
      
      // Header
      doc.setFontSize(22);
      doc.setTextColor(184, 137, 58); // Gold color
      doc.text("AWS BUSINESSMEN SERVICES", 14, 20);
      
      doc.setFontSize(14);
      doc.setTextColor(50, 50, 50);
      doc.text("Cost Estimate Invoice", 14, 30);
      
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      const date = new Date().toLocaleDateString();
      doc.text(`Date: ${date}`, 14, 38);
      
      // Convert line items to table rows
      const tableData = lineItems.map(item => [item.label, formatAED(item.amount)]);
      
      // Add total row at the end
      tableData.push(["Total Estimated Cost", formatAED(total)]);

      // Use autoTable explicitly rather than doc.autoTable to avoid missing prototype
      autoTable(doc, {
        startY: 45,
        head: [["Description", "Amount (AED)"]],
        body: tableData,
        theme: 'grid',
        headStyles: { fillColor: [184, 137, 58], textColor: 255, fontStyle: 'bold' },
        styles: { fontSize: 10, cellPadding: 6 },
        alternateRowStyles: { fillColor: [249, 250, 251] },
        margin: { top: 45 },
        didParseCell: function (data) {
          // Highlight the last row (Total)
          if (data.section === 'body' && data.row.index === tableData.length - 1) {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fillColor = [240, 240, 240];
            data.cell.styles.textColor = [0, 0, 0];
          }
        }
      });
      
      // Footer message
      // Safe guard finalY retrieval - autoTable attaches it to doc context sometimes or as a property.
      const finalY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 15 : 150;
      doc.setFontSize(9);
      doc.setTextColor(120, 120, 120);
      doc.text("Disclaimer: This is an indicative estimate generated by our online calculator. ", 14, finalY);
      doc.text("Please contact our specialists for a final itemised proposal and confirmed pricing.", 14, finalY + 5);

      // Open PDF in a new tab for review
      const pdfBlobUrl = doc.output("bloburl");
      window.open(pdfBlobUrl, "_blank");

    } catch (error) {
      console.error("Failed to generate PDF:", error);
      alert("There was an issue generating the PDF. Please try again.");
    }
  };

  return (
    <div className="w-full">
      <StepLabel
        tag="Your Estimate"
        title="Here's your cost breakdown"
        description="Indicative pricing based on your selections. A specialist will provide a precise itemised proposal."
      />

      {/* Total banner */}
      <div className="bg-black rounded-2xl px-5 py-4 mb-6 text-center sm:text-left">
        <p className="text-xs text-gray-400 uppercase tracking-widest mb-2">
          Estimated Total
        </p>

        <p 
          className="font-black text-[#B8893A] break-words"
          style={{ fontSize: "clamp(24px, 4vw, 36px)" }}
        >
          {formatAED(total)}
        </p>

        <p 
          className="text-gray-500 mt-2"
          style={{ fontSize: "clamp(10px, 0.8vw, 12px)" }}
        >
          All figures in AED · indicative only
        </p>
      </div>

      {/* Line items */}
      <div className="space-y-2 mb-6">
        {lineItems.map((item) => (
          <div
            key={item.label}
            className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 sm:gap-0
                       py-3 px-4 bg-gray-50 rounded-xl"
          >
            <span className="text-sm text-gray-600">{item.label}</span>
            <span className="text-sm font-semibold text-gray-800">
              {formatAED(item.amount)}
            </span>
          </div>
        ))}
      </div>

      {/* CTAs */}
      <div className="space-y-3">
        <a
          href="tel:+971586009983"
          className="flex items-center justify-center gap-2 w-full py-3 rounded-xl
                     bg-[#B8893A] text-white font-bold text-sm
                     hover:bg-[#a07830] active:scale-[0.98] transition
                     shadow-lg shadow-[#B8893A]/20"
        >
          Get My Custom Proposal
          <ArrowRight size={16} />
        </a>
        
        <button
          onClick={handlePreviewInvoice}
          className="flex items-center justify-center gap-2 w-full py-3 rounded-xl
                     border-2 border-[#B8893A] text-[#B8893A] font-bold text-sm
                     hover:bg-[#B8893A]/10 active:scale-[0.98] transition"
        >
          <FileText size={16} />
          Preview & Download PDF
        </button>

      <a
  href="https://wa.me/971507987470"
  target="_blank"
  rel="noopener noreferrer"
  className="flex items-center justify-center w-full py-3 rounded-xl
             border border-gray-200 text-sm font-semibold text-gray-700
             hover:bg-gray-50 active:scale-[0.98] transition"
>
  Talk to a Specialist
</a>
      </div>

      <p className="text-center text-xs text-gray-400 mt-5 px-2">
        No obligation · Free consultation · 100% confidential
      </p>
    </div>
  );
}
